import { safeJson } from "src/app/helpers/Index";
import { faGamepadModern, faBuilding, faCalendarAlt, faCar, faChartTreeMap, faFileContract, faHandshake, faHouseBlank, faImage, faLightbulb, faLock, faMoneyBill, faPeopleGroup, faSignOut, faSliders, faStopwatch, faTicket, faLightsHoliday, faGift, faCalendar, faUserTie, faEye, faDisplay, faIdCard, faTrophy, faChevronDown, faReceipt, faCalendarStar, faLocationDot, faLayerGroup, faCartPlus, faTag, faQrcode } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "primereact/badge";
import React from "react";
import { Link, Navigate, NavLink, Outlet } from "react-router-dom";
import { AppSettingsService, SeasonService, UserService } from "src/app/services/Index";
import { colours } from "src/app/Styles";
import { environment } from "src/environments/environment";
import "./AdminMain.css";
import "../seasons/AdminSeasons.css";
import { CheckGroupPermissions } from "src/app/helpers/CheckGroupPermissions";
import { ClubContext, HasFeature, LoadClubContext, SetAdminRouteFromLocation } from "src/app/helpers/ClubContext";

type Props = {};
type State = {
	club: any;
	season: any;
	currentUser: any;
	isLoggedIn?: boolean;
	mobileTopbarMenuClick: boolean;
	menuItems: any[];
	collapsedGroups: string[];
};

/**
 * Collapsed sidebar groups, remembered between visits. Someone who never
 * touches ticketing shouldn't have to scroll past it every time they sign in.
 */
const COLLAPSED_KEY = "pb-admin-collapsed-groups";

const loadCollapsed = (): string[] => {
	try {
		const raw = window.localStorage.getItem(COLLAPSED_KEY);
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};
export class AdminMain extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { club: null, collapsedGroups: loadCollapsed(), currentUser: null, mobileTopbarMenuClick: false, menuItems: [], season: null };
	}

	componentDidMount() {
		document.documentElement.classList.add("admin-zoom");
		SetAdminRouteFromLocation();
		this.loadClub();
		this.loadSeason();
		this.isUserLoggedIn();
		this.getMenuItems();
		this.getMe();
	}

	/** Club identity for the topbar (name + badge), from the tenant context. */
	async loadClub() {
		await LoadClubContext();
		if (ClubContext.club) this.setState({ club: ClubContext.club });
	}

	/**
	 * The current season, shown in the topbar. Which season new fixtures land
	 * in is easy to get wrong and expensive to notice late, so it is on screen
	 * rather than buried in settings. Fails quietly — a club without seasons
	 * set up shouldn't see a broken topbar.
	 */
	async loadSeason() {
		try {
			const res = await safeJson(SeasonService.getCurrent());
			if (res?.available) this.setState({ season: res.season });
		} catch { /* no seasons yet */ }
	}

	async getMe() {
		// A 401 comes back with an empty body, and .json() on that throws. The
		// topbar name is not worth taking the screen down for.
		try {
			const response = await UserService.me();
			if (!response.ok) return;

			const res = await response.json();
			if (res && !res.error) {
				this.setState({ currentUser: res });
			}
		} catch { /* topbar falls back to no name */ }
	}

	async getMenuItems() {
		let menuItems = [];

		// Which products this club has bought. Read here rather than relying on
		// loadClub having finished — the two run concurrently, and a menu that
		// depends on a race would show ticketing to half the clubs half the time.
		await LoadClubContext();

		let homeMenuItems = [
			{ icon: faHouseBlank, label: "Dashboard", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}` }
			// Holiday features hidden from the menu (pages still reachable by URL)
			// { icon: faCalendar, label: "My Holidays", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/my-holiday-requests` }
		];

		menuItems.push({
			label: "Home",
			items: homeMenuItems
		});

		// What the club has on. Fixtures is one screen now — the calendar, the
		// ticketing setup and the matchday editor — so it belongs to the club
		// rather than to either feature that reads it.
		let scheduleMenuItems: any[] = [
			{ icon: faCalendarAlt, label: "Fixtures", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/schedule/fixtures` }
		];

		// Events are ticketed-only; a scoreboard club has no use for them.
		if (HasFeature("Ticketing")) {
			scheduleMenuItems.push({ icon: faCalendarStar, label: "Events", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/schedule/events` });
			// Sits with the schedule rather than under ticketing: on a matchday
			// whoever is on the gate is thinking about the game, not about the
			// product that sold the ticket.
			scheduleMenuItems.push({ icon: faQrcode, label: "Scan tickets", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/scanning` });
		}

		if (HasFeature("Scoreboard", true) || HasFeature("Ticketing")) {
			menuItems.push({
				label: "Schedule",
				items: scheduleMenuItems
			});
		}

		// Supporters are the club's own, whatever else it has switched on.
		let fanMenuItems: any[] = [
			{ icon: faPeopleGroup, label: "Fan profiles", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/fans` },
			{ icon: faEye, label: "Audiences", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/audiences` }
		];

		menuItems.push({
			label: "Fans",
			items: fanMenuItems
		});

		let scoreboardItems = [
			{ icon: faGamepadModern, label: "Remote", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/remote` },
			{ icon: faPeopleGroup, label: "Teams", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/teams` },
			{ icon: faChartTreeMap, label: "Formation Manager", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/formation` },
			{ icon: faImage, label: "Resources", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/resources` },
			{ icon: faDisplay, label: "Scoreboards", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/boards` }
			// Scoreboard theme/layout still lives in Club Settings -> Scoreboard tab
		]

		// Scoreboard defaults ON for clubs that predate the flags — matches the API.
		if (HasFeature("Scoreboard", true) && await CheckGroupPermissions("Scoreboard Management")) {
			menuItems.push({
				label: "Scoreboard Management",
				items: scoreboardItems
			});
		};
		let venueMenuItems: any[] = [];
	// 	if (await CheckGroupPermissions("Venue Management")) {
	// 		venueMenuItems.push(
	// 		// {icon: faTicket, label: "Tickets", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/tickets`},
	// 		{ icon: faCar, label: "Parking", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/parking` },
	// 		{ icon: faChartTreeMap, label: "Layout Editor", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/parking-editor` }
	// 		);
	// };

		// Visitors hidden from the menu (page still reachable at /home/visitors)
		// if (await CheckGroupPermissions("Meeting Managers")) {
		// 	venueMenuItems.push(
		// 		{ icon: faUserTie, label: "Visitors", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/visitors` },
		// 	);
		// }

		if (venueMenuItems.length > 0 && (await CheckGroupPermissions("Venue Management") || await CheckGroupPermissions("Meeting Managers"))) {
			menuItems.push({
				label: "Venue Management",
				items: venueMenuItems
			});
		};

		let relationshipMenuItems = [{ icon: faBuilding, label: "Sponsors", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/sponsors` }];

		// if (await CheckGroupPermissions("Super Administrators")) {
		// 	relationshipMenuItems.push({icon: faStopwatch, label: "Sponsorship Types", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/sponsorship-types`});
		// }

		// relationshipMenuItems.push(
		// 	{icon: faMoneyBill, label: "Sponsorships", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}`},
		// 	{icon: faHandshake, label: "Partners", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/partners`},
		// 	{icon: faFileContract, label: "Service Contracts", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/service-contracts`}
		// );
		if (await CheckGroupPermissions("Commercial")) {
			menuItems.push({
				label: "Relationship Management",
				items: relationshipMenuItems
			});
		};

		// Holiday features hidden from the menu (pages still reachable by URL)
		// let holidayItems = [
		// 	{ icon: faEye, label: "Holidays Planner", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/holiday-schedule` },
		// 	{ icon: faLightsHoliday, label: "Company Holidays", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/company-holidays` },
		// 	{ icon: faGift, label: "Holiday Allowances", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/holiday-allowances` },
		// 	{ icon: faCalendar, label: "Holiday Requests", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/holiday-requests` },
		// ];

		// if (await CheckGroupPermissions("Holiday Managers")) {
		// 	menuItems.push({
		// 		label: "Holiday Management",
		// 		items: holidayItems
		// 	});
		// };

		let ticketingMenuItems = [
			{ icon: faTicket, label: "Ticket Types", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/ticketing/types` },
			{ icon: faLayerGroup, label: "Pricing", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/ticketing/pricing` },
			{ icon: faLocationDot, label: "Venues & Seating", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/venues` },
			{ icon: faChartTreeMap, label: "Seating Plans", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/seat-maps` },
			{ icon: faIdCard, label: "Season Tickets", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/ticketing/season-tickets` },
			{ icon: faReceipt, label: "Hospitality", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/ticketing/hospitality` },
			{ icon: faPeopleGroup, label: "Memberships", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/ticketing/memberships` },
			{ icon: faCartPlus, label: "Add-ons", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/ticketing/add-ons` },
			{ icon: faTag, label: "Promo Codes", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/ticketing/promo-codes` },
			{ icon: faTrophy, label: "Competitions", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/ticketing/competitions` }
		];

		// Ticketing is opt-in per club. Off means the group simply isn't there —
		// better than a menu full of screens the club hasn't bought.
		if (HasFeature("Ticketing") && await CheckGroupPermissions("Super Administrators")) {
			menuItems.push({
				label: "Ticketing",
				items: ticketingMenuItems
			});
		}

		let systemMenuItems = [];

		if (await CheckGroupPermissions("Super Administrators")) {
			systemMenuItems.push(
				{ icon: faBuilding, label: "Club Settings", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/club-settings` },
				{ icon: faCalendarStar, label: "Seasons", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/seasons` },

				{ icon: faLightbulb, label: "Users", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/users` },
				{ icon: faReceipt, label: "Billing", template: (item: any) => this.renderMenuItem(item), to: `/${environment.build.adminRoute}/billing` }
				// System Settings hidden from the menu (page still reachable at /home/system-settings)
			);
		}



	

		// Sign Out lives in the topbar now
		if (systemMenuItems.length > 0) {
			menuItems.push({
				label: "System",
				items: systemMenuItems
			});
		}

		this.setState({ menuItems });
	}

	/**
	 * Are we signed in?
	 *
	 * Must always reach an answer. render() draws nothing until this resolves,
	 * so anything that leaves it unset is a permanently blank admin — which is
	 * exactly what an expired session used to produce: the check 401s with an
	 * empty body, .json() throws on it, and the screen never recovers.
	 *
	 * Every failure therefore means "not signed in", which shows the login
	 * screen. The worst case is asking somebody to sign in again.
	 */
	async isUserLoggedIn() {
		try {
			const response = await UserService.loginCheck("Administrators");
			if (!response.ok) {
				this.setState({ isLoggedIn: false });
				return;
			}

			const res = await response.json();
			this.setState({ isLoggedIn: res === true });
		} catch {
			this.setState({ isLoggedIn: false });
		}
	}

	mobileTopbarMenuClick(event: any) {
		this.setState({ mobileTopbarMenuClick: !this.state.mobileTopbarMenuClick });
		event.preventDefault();
	}

	componentWillUnmount() {
		document.documentElement.classList.remove("admin-zoom");
	}

	render() {
		if (this.state.isLoggedIn === true) return this.renderMain();
		else if (this.state.isLoggedIn === false) return this.renderLogin();
		else return;
	}

	/**
	 * Not signed in — send them to the login screen.
	 *
	 * Must point at /login, not the admin root. adminRoute is rewritten from
	 * the URL on mount, so the root IS this page: redirecting there sent the
	 * shell to itself, over and over, drawing nothing. A blank admin was the
	 * visible half of that.
	 */
	renderLogin() {
		return (
			<Navigate replace={true} to={`/${environment.build.adminRoute}/login`} />
		);
	}

	renderMain() {
		return (
			<div style={{ backgroundColor: "var(--pb-bg)", minHeight: "100vh", paddingTop: "4.5rem" }}>
				<div className="layout-topbar" style={{ width: "100vw" }}>
					<button className="topbar-hamburger" onClick={(event) => this.mobileTopbarMenuClick(event)} type="button">
						<i className={`pi ${this.state.mobileTopbarMenuClick ? "pi-times" : "pi-bars"}`} />
					</button>
					<Link className="layout-topbar-logo" style={{ textDecoration: "none", whiteSpace: "nowrap", width: "auto" }} to={`/${environment.build.adminRoute}`}>
						<img alt="Pitchbase" src="/assets/pitchbase-icon.png" style={{ borderRadius: 8, height: "2.2rem", marginRight: "0.75rem" }} />
						<span style={{ fontWeight: 800 }}>Pitchbase</span>
						<span className="topbar-divider" style={{ backgroundColor: "rgba(255, 255, 255, 0.35)", height: "1.6rem", margin: "0 1rem", width: 1 }} />
						{this.state.club?.badgeUrl &&
							<img alt="" className="topbar-clubbadge" src={this.state.club.badgeUrl} />}
						<span className="topbar-clubname">{(this.state.club?.name || "").toUpperCase()}</span>
						{this.state.season &&
							<span className="topbar-season" title="New fixtures go into this season">{this.state.season.name}</span>}
					</Link>
					<div className="topbar-user">
						{this.state.currentUser &&
							<Link className="topbar-user-link" title="My profile" to={`/${environment.build.adminRoute}/profile`}>
								<div className="topbar-user-avatar">{`${(this.state.currentUser.firstName || " ")[0]}${(this.state.currentUser.lastName || " ")[0]}`.trim().toUpperCase()}</div>
								<span className="topbar-user-name">{[this.state.currentUser.lastName, this.state.currentUser.firstName].filter(Boolean).join(", ")}</span>
							</Link>
						}
						<button className="topbar-signout" onClick={() => this.signOut()} title="Sign Out" type="button">
							<FontAwesomeIcon icon={faSignOut}/>
						</button>
					</div>
				</div>
				{this.state.mobileTopbarMenuClick && <div className="pb-sidebar-backdrop" onClick={() => this.setState({ mobileTopbarMenuClick: false })} />}
				<nav className={`pb-sidebar${this.state.mobileTopbarMenuClick ? " open" : ""}`}>
					{this.state.menuItems.map((group: any) => {
						const collapsed = this.state.collapsedGroups.includes(group.label);
						return (
							<div key={`nav-${group.label}`}>
								<button
									aria-expanded={!collapsed}
									className={`pb-sidebar-heading${collapsed ? " collapsed" : ""}`}
									onClick={() => this.toggleGroup(group.label)}
									type="button"
								>
									<span>{group.label}</span>
									<FontAwesomeIcon className="pb-sidebar-chevron" icon={faChevronDown} />
								</button>
								{!collapsed && group.items.map((item: any, i: number) => <React.Fragment key={`nav-${group.label}-${i}`}>{item.template(item)}</React.Fragment>)}
							</div>
						);
					})}
				</nav>
				<div className="admin-content" style={{ marginLeft: "22.75rem", marginRight: "2rem", marginTop: "2rem", paddingBottom: "2rem", width: "calc(100% - 24.75rem)" }}>
					<Outlet />
				</div>
			</div>
		);
	}

	toggleGroup(label: string) {
		const collapsedGroups = this.state.collapsedGroups.includes(label)
			? this.state.collapsedGroups.filter((g) => g !== label)
			: [...this.state.collapsedGroups, label];
		this.setState({ collapsedGroups });
		try {
			window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsedGroups));
		} catch { /* private browsing — the menu just won't remember */ }
	}

	renderMenuItem(item: any) {
		return (
			<NavLink className="pb-nav-item" end onClick={() => this.setState({ mobileTopbarMenuClick: false })} to={item.to}>
				<span className="pb-nav-icon"><FontAwesomeIcon icon={item.icon}/></span>
				<span>{item.label}</span>
				{item.badge &&
					<Badge style={{ backgroundColor: colours.primary, marginLeft: "auto" }} value={item.badge}></Badge>
				}
			</NavLink>
		);
	}

	async signOut() {
		await AppSettingsService.remove("accessToken");
		await AppSettingsService.remove("refreshToken");
		await AppSettingsService.remove("groups");
		this.setState({ isLoggedIn: false });
	}
}